import { Dialog, DialogRef } from "@angular/cdk/dialog";
import { inject, Injectable } from "@angular/core";
import { AlertComponent } from "../components/alert/alert.component";
import { AlertOptions, AlertResult } from "../components/alert/alert.type";

@Injectable({ providedIn: "root" })
export class AlertService {
    private readonly dialog = inject(Dialog);
    private currentDialogRef?: DialogRef<AlertResult>;
    private currentMessage?: string;

    info(message: string, title = "알림"): DialogRef<AlertResult> {
        return this.open({
            title,
            content: message,
            type: "info",
            buttons: {
                confirm: {
                    color: "primary",
                },
            },
        });
    }

    warning(message: string, title = "알림"): DialogRef<AlertResult> {
        return this.open({
            title,
            content: message,
            type: "warning",
            buttons: {
                confirm: {
                    color: "yellow",
                },
            },
        });
    }

    error(message: string, title = "알림"): DialogRef<AlertResult> {
        return this.open({
            title,
            content: message,
            type: "danger",
            buttons: {
                confirm: {
                    color: "red",
                },
            },
        });
    }

    open(options: AlertOptions): DialogRef<AlertResult> {
        // ✅ 이미 같은 내용의 Alert이 떠 있으면 재호출 무시
        if (this.currentDialogRef && this.currentMessage === options.content) {
            return this.currentDialogRef;
        }

        // ✅ 기존 Alert이 떠 있으면 닫고 새 Alert을 띄움 (덮어쓰기)
        if (this.currentDialogRef) {
            this.currentDialogRef.close();
        }

        // ✅ 새 다이얼로그 열기
        const ref = this.dialog.open<AlertResult>(AlertComponent, {
            maxHeight: "100%",
            data: options,
            disableClose: true,
            ariaModal: true,
            restoreFocus: true,
        });

        // 상태 저장
        this.currentDialogRef = ref;
        this.currentMessage = options.content;

        // 닫힐 때 상태 초기화
        ref.closed.subscribe(() => {
            this.currentDialogRef = undefined;
            this.currentMessage = undefined;
        });

        return ref;
    }

    async asyncOpen(options: AlertOptions): Promise<AlertResult | undefined> {
        const ref = this.open(options);
        const result = await ref.closed.toPromise();
        return result;
    }
}
